import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  Brain,
  Download,
  FileCode,
  FileText,
  FolderOpen,
  Image,
  Link2,
  MoreHorizontal,
  Plus,
  Presentation,
  Search,
  Share2,
  StickyNote,
  Sigma,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import Sidebar from "@/pages/home/components/Sidebar";
import KnowledgeBaseCard from "./components/KnowledgeBaseCard";
import CreateKBModal from "./components/CreateKBModal";
import type { KnowledgeBase } from "@/mocks/knowledge";
import { deleteKnowledgeBase, loadKnowledgeBases, renameKnowledgeBase, saveKnowledgeBases } from "./store";
import { createEmptyNoteDoc, ensureSeedNotes, listNoteDocs, upsertNoteDoc } from "./note/note-store";
import { INITIAL_ACHIEVEMENTS, achievementTypeLabel, type AchievementRow, type AchievementType } from "./achievements/mock";

const mainTabs = ["知识", "成果", "笔记"] as const;
const knowledgeTabs = ["全部", "我创建的", "我加入的"] as const;
const achievementTabs = ["全部", "html", "pdf", "图片", "word", "ppt"] as const;


type MainTab = (typeof mainTabs)[number];
type NoteType = "笔记";
type Note = {
  id: string;
  title: string;
  content: string;
  date: string;
  type: NoteType;
  tags: string[];
  icon: typeof StickyNote;
  color: string;
  bg: string;
  wordCount: number;
};


const NOTE_ICON = StickyNote;
const NOTE_COLOR = "text-primary";
const NOTE_BG = "bg-muted/35";

function autoTagFromName(name: string): string {
  const v = (name || "").trim();
  if (!v) return "知识";
  const cjk = Array.from(v).filter((ch) => /[\u4e00-\u9fff]/.test(ch));
  if (cjk.length >= 2) return `${cjk[0]}${cjk[1]}`;
  if (cjk.length === 1) {
    const rest = Array.from(v).filter((ch) => /[A-Za-z0-9]/.test(ch));
    return `${cjk[0]}${(rest[0] || "库").toString().toUpperCase()}`.slice(0, 2);
  }
  const alnum = Array.from(v).filter((ch) => /[A-Za-z0-9]/.test(ch));
  if (alnum.length >= 2) return `${alnum[0]}${alnum[1]}`.toUpperCase();
  return v.slice(0, 2);
}

export default function KnowledgePage() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeMain, setActiveMain] = useState<MainTab>("知识");
  const [searchVal, setSearchVal] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [knowledgeFilter, setKnowledgeFilter] = useState<(typeof knowledgeTabs)[number]>("全部");
  const [achievementFilter, setAchievementFilter] = useState<(typeof achievementTabs)[number]>("全部");
  const [showNewNote, setShowNewNote] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [renamingNoteId, setRenamingNoteId] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState("");
  const [shareNoteId, setShareNoteId] = useState<string | null>(null);
  const [moreMenuNoteId, setMoreMenuNoteId] = useState<string | null>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const [notes, setNotes] = useState<Note[]>(() => {
    ensureSeedNotes();
    return listNoteDocs().map((d) => ({
      id: d.id,
      title: d.title,
      content: d.subtitle || d.content.slice(0, 80),
      date: d.updatedAt,
      type: "笔记",
      tags: d.tags,
      icon: NOTE_ICON,
      color: NOTE_COLOR,
      bg: NOTE_BG,
      wordCount: (d.content || "").replace(/\s/g, "").length,
    }));
  });
  const [kbList, setKbList] = useState<KnowledgeBase[]>(() => loadKnowledgeBases());
  const [deleteKbTarget, setDeleteKbTarget] = useState<{ id: string; name: string } | null>(null);
  const [achievements, setAchievements] = useState<AchievementRow[]>(INITIAL_ACHIEVEMENTS);
  const [previewAchievementId, setPreviewAchievementId] = useState<string | null>(null);
  const [achievementShareId, setAchievementShareId] = useState<string | null>(null);
  const [storeAchievementId, setStoreAchievementId] = useState<string | null>(null);
  const [storeNoteId, setStoreNoteId] = useState<string | null>(null);
  const [storeKbPickId, setStoreKbPickId] = useState<string | null>(null);
  const [storeKbRelationFilter, setStoreKbRelationFilter] = useState<(typeof knowledgeTabs)[number]>("全部");
  const [deleteAchievementTarget, setDeleteAchievementTarget] = useState<{ id: string; title: string } | null>(null);
  const [actionToast, setActionToast] = useState<string | null>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setMoreMenuNoteId(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!achievementShareId) return;
    const close = (e: MouseEvent) => {
      const root = (e.target as HTMLElement).closest("[data-achievement-share]");
      if (!root) setAchievementShareId(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [achievementShareId]);

  useEffect(() => {
    if (!actionToast) return;
    const t = window.setTimeout(() => setActionToast(null), 2800);
    return () => window.clearTimeout(t);
  }, [actionToast]);

  const filteredKnowledge = useMemo(() => {
    let list = kbList;
    if (knowledgeFilter === "我创建的") {
      list = list.filter((kb) => kb.relation !== "joined");
    } else if (knowledgeFilter === "我加入的") {
      list = list.filter((kb) => kb.relation === "joined");
    }
    if (searchVal.trim()) {
      const q = searchVal.toLowerCase();
      list = list.filter((kb) => kb.name.toLowerCase().includes(q));
    }
    return list;
  }, [kbList, knowledgeFilter, searchVal]);

  const filteredNotes = useMemo(() => notes, [notes]);

  // 每次回到该页时，从“后台”(sessionStorage) 同步一次列表，确保未保存也可见
  useEffect(() => {
    ensureSeedNotes();
    setNotes(
      listNoteDocs().map((d) => ({
        id: d.id,
        title: d.title,
        content: d.subtitle || d.content.slice(0, 80),
        date: d.updatedAt,
        type: "笔记",
        tags: d.tags,
        icon: NOTE_ICON,
        color: NOTE_COLOR,
        bg: NOTE_BG,
        wordCount: (d.content || "").replace(/\s/g, "").length,
      })),
    );
  }, [activeMain]);

  const filteredAchievements = useMemo(
    () => achievements.filter((item) => achievementFilter === "全部" || item.type === achievementFilter),
    [achievementFilter, achievements],
  );

  const storeModalKbList = useMemo(() => {
    let list = kbList;
    if (storeKbRelationFilter === "我创建的") {
      list = list.filter((kb) => kb.relation !== "joined");
    } else if (storeKbRelationFilter === "我加入的") {
      list = list.filter((kb) => kb.relation === "joined");
    }
    return list;
  }, [kbList, storeKbRelationFilter]);

  const storeAchievement = storeAchievementId ? achievements.find((a) => a.id === storeAchievementId) : undefined;
  const storeNote = storeNoteId ? notes.find((n) => n.id === storeNoteId) : undefined;
  const previewAchievement = previewAchievementId ? achievements.find((a) => a.id === previewAchievementId) : undefined;

  const closeStoreModal = () => {
    setStoreAchievementId(null);
    setStoreNoteId(null);
    setStoreKbPickId(null);
    setStoreKbRelationFilter("全部");
  };

  useEffect(() => {
    if (!storeAchievementId && !storeNoteId) return;
    if (storeModalKbList.length === 0) {
      setStoreKbPickId(null);
      return;
    }
    if (!storeKbPickId || !storeModalKbList.some((k) => k.id === storeKbPickId)) {
      setStoreKbPickId(storeModalKbList[0]!.id);
    }
  }, [storeAchievementId, storeNoteId, storeModalKbList, storeKbPickId]);

  const showToast = (msg: string) => setActionToast(msg);

  const closePreview = () => setPreviewAchievementId(null);

  const renderAchievementPreview = (item: AchievementRow) => {
    if (item.type === "pdf") {
      const pdfPages = [
        {
          title: "摘要与关键结论",
          bullets: ["综合评分 0.82（置信区间 0.78–0.86）", "主要提升项：误差抑制 + 译码策略", "建议：优先验证 3 个关键实验条件"],
        },
        {
          title: "方法与数据来源",
          bullets: ["数据集：5 篇核心论文 + 2 套开源实现", "对比基线：Surface code / Bacon–Shor", "指标：逻辑错误率、门深度、资源开销"],
        },
        {
          title: "结果可视化",
          bullets: ["在 p=1e-3 下逻辑错误率降低 23%", "译码延迟降低 18ms（P95）", "资源开销 +6%（可接受）"],
        },
        {
          title: "附录：复现实验",
          bullets: ["环境：Python 3.11 / qec-sim 0.9", "随机种子：20260415", "脚本：run_eval.py / plot.ipynb"],
        },
      ];
      return (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">PDF 预览（演示）</div>
              <div className="mt-1 text-sm text-muted-foreground">模拟真实报告结构（摘要 / 方法 / 结果 / 附录）。</div>
            </div>
            <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">PDF</span>
          </div>
          <div className="rounded-2xl border border-border bg-muted/10 p-4">
            <div className="grid gap-3 md:grid-cols-2">
              {pdfPages.map((p, idx) => (
                <div key={p.title} className="rounded-xl border border-border bg-background p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-semibold text-foreground">
                      第 {idx + 1} 页 · {p.title}
                    </div>
                    <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-[10px] text-muted-foreground">A4</span>
                  </div>
                  <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                    {p.bullets.map((b) => (
                      <div key={b} className="flex gap-2">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                        <span className="leading-relaxed">{b}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 rounded-lg border border-border bg-muted/10 p-3">
                    <div className="text-[10px] font-medium text-muted-foreground">图表（演示）</div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {[0.68, 0.82, 0.74].map((v, i) => (
                        <div key={i} className="flex items-end gap-1 rounded-md bg-background p-2">
                          <div className="h-10 w-2 rounded bg-muted/50" />
                          <div className="w-2 rounded bg-primary/70" style={{ height: `${Math.round(v * 40)}px` }} />
                          <div className="ml-auto text-[10px] font-semibold text-foreground">{Math.round(v * 100)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (item.type === "html") {
      const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto; padding: 16px; color: #1D2129; }
      .card { border: 1px solid #E5E6EB; border-radius: 14px; padding: 14px; background: #fff; }
      .muted { color: #646479; font-size: 12px; }
      .bar { height: 8px; background: rgba(61,105,232,.15); border-radius: 999px; overflow: hidden; margin-top: 10px; }
      .bar > div { height: 100%; width: 68%; background: rgba(61,105,232,.85); }
    </style>
  </head>
  <body>
    <div class="card">
      <div style="font-weight: 700;">交互式 HTML 成果预览（演示）</div>
      <div class="muted" style="margin-top: 6px;">后续可替换为实际生成的 HTML/可视化 dashboard。</div>
      <div class="bar"><div></div></div>
      <div class="muted" style="margin-top: 10px;">模块：筛选器 / 指标卡 / 图表</div>
    </div>
  </body>
</html>`;
      return (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">HTML 可视化预览</div>
              <div className="mt-1 text-sm text-muted-foreground">使用内嵌 iframe 渲染（演示内容）。</div>
            </div>
            <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">HTML</span>
          </div>
          <div className="overflow-hidden rounded-2xl border border-border bg-background">
            <iframe title="achievement-html-preview" className="h-[420px] w-full" sandbox="allow-scripts" srcDoc={html} />
          </div>
        </div>
      );
    }

    if (item.type === "图片") {
      const imgs = [
        { title: "能量分布热图", note: "T=300K，步长 1fs，采样 50k steps" },
        { title: "轨迹片段（3D）", note: "关键构型：局部重排与缺陷迁移" },
        { title: "RDF 曲线对比", note: "A/B 两组参数对结构差异的影响" },
        { title: "应力-应变曲线", note: "屈服点附近波动，建议增加平滑窗口" },
        { title: "聚类结果", note: "K=6，轮廓系数 0.41" },
        { title: "参数敏感性", note: "对截断半径 r_c 最敏感" },
      ];
      return (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">图片图集预览（演示）</div>
              <div className="mt-1 text-sm text-muted-foreground">模拟 6 张图的标题与说明（非加载态）。</div>
            </div>
            <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">Images</span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {imgs.map((im, i) => (
              <div key={im.title} className="overflow-hidden rounded-xl border border-border bg-background">
                <div
                  className="aspect-[4/3]"
                  style={{
                    background:
                      i % 3 === 0
                        ? "linear-gradient(135deg, rgba(61,105,232,.22), rgba(61,105,232,0))"
                        : i % 3 === 1
                          ? "linear-gradient(135deg, rgba(61,105,232,.12), rgba(61,105,232,.02))"
                          : "linear-gradient(135deg, rgba(61,105,232,.18), rgba(61,105,232,0))",
                  }}
                />
                <div className="border-t border-border p-3">
                  <div className="text-xs font-semibold text-foreground">
                    {i + 1}. {im.title}
                  </div>
                  <div className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{im.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (item.type === "word") {
      return (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground">Word 文档预览（演示）</div>
              <div className="mt-1 text-sm text-muted-foreground">模拟“实验记录与参数表”的真实结构（标题 / 文字 / 表格）。</div>
            </div>
            <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">DOCX</span>
          </div>
          <div className="rounded-2xl border border-border bg-background p-5">
            <div className="space-y-4">
              <div>
                <div className="text-base font-semibold text-foreground">实验记录与参数表</div>
                <div className="mt-1 text-sm text-muted-foreground">记录编号：EXP-2026-0406 · 版本：v1.3</div>
              </div>
              <p className="text-sm leading-7 text-muted-foreground">
                本文档汇总了关键实验条件、仪器参数与异常记录，用于组内复核与归档。建议在每次跑完实验后补充“偏差原因”和“下一步验证”两列，便于形成可追溯闭环。
              </p>

              <div className="rounded-2xl border border-border bg-muted/10 p-4">
                <div className="text-sm font-semibold text-foreground">参数表（节选）</div>
                <div className="mt-3 overflow-hidden rounded-xl border border-border bg-background">
                  <div className="grid grid-cols-4 gap-0 border-b border-border bg-muted/20 px-3 py-2 text-xs font-medium text-muted-foreground">
                    <div>参数</div>
                    <div>设定值</div>
                    <div>实测值</div>
                    <div>备注</div>
                  </div>
                  {[
                    ["温度", "300K", "301.2K", "稳定 ±0.6K"],
                    ["压力", "1 atm", "0.98 atm", "泵压波动"],
                    ["步长", "1 fs", "1 fs", "固定"],
                    ["采样", "50k steps", "48.7k", "中途重启一次"],
                  ].map((row) => (
                    <div key={row[0]} className="grid grid-cols-4 gap-0 px-3 py-2 text-xs text-foreground">
                      <div className="text-muted-foreground">{row[0]}</div>
                      <div>{row[1]}</div>
                      <div className="font-medium">{row[2]}</div>
                      <div className="text-muted-foreground">{row[3]}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // ppt
    return (
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-foreground">PPT 演示预览（演示）</div>
            <div className="mt-1 text-sm text-muted-foreground">模拟 4 页汇报的真实内容（标题 / 要点 / 图表）。</div>
          </div>
          <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-xs font-medium text-muted-foreground">PPT</span>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          {[
            { t: "研究进展概览", b: ["里程碑完成度 72%", "核心指标：P95 延迟 180ms", "下一步：补齐对照实验"] },
            { t: "关键实验结果", b: ["逻辑错误率下降 23%", "资源开销 +6%", "稳定性提升：std ↓ 12%"] },
            { t: "风险与对策", b: ["合规条款需审查", "样本口径需统一", "建议提前冻结指标口径"] },
            { t: "计划与排期", b: ["本周：复现实验", "下周：补齐图表", "月底：输出终稿"] },
          ].map((s, i) => (
            <div key={i} className="overflow-hidden rounded-xl border border-border bg-background">
              <div className="aspect-[16/9] bg-muted/10 p-4">
                <div className="text-xs font-semibold text-foreground">Slide {i + 1}</div>
                <div className="mt-1 text-sm font-semibold text-foreground">{s.t}</div>
                <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                  {s.b.map((x) => (
                    <div key={x} className="flex gap-2">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
                      <span className="leading-relaxed">{x}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-primary/70" style={{ width: `${60 + i * 8}%` }} />
                </div>
              </div>
              <div className="border-t border-border p-3">
                <div className="text-xs font-medium text-muted-foreground">备注：可在此处接入真实 pptx 渲染</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleCopyAchievementLink = (item: AchievementRow) => {
    const link = `${window.location.origin}${window.location.pathname}?achievement=${encodeURIComponent(item.id)}`;
    void navigator.clipboard.writeText(link).then(
      () => {
        showToast("分享链接已复制到剪贴板");
        setAchievementShareId(null);
      },
      () => showToast("复制失败，请手动复制链接"),
    );
  };

  const handleDownloadAchievement = (item: AchievementRow) => {
    const body = `${item.title}\n\n${item.desc}\n\n类型：${achievementTypeLabel[item.type]}\n日期：${item.date}`;
    const blob = new Blob([body], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${item.title.replace(/[/\\?%*:|"<>]/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("已开始下载");
  };

  const handleOpenStoreModal = (achievementId: string) => {
    setStoreAchievementId(achievementId);
    setStoreNoteId(null);
    setStoreKbRelationFilter("全部");
    setStoreKbPickId(kbList[0]?.id ?? null);
  };

  const handleOpenStoreNoteModal = (noteId: string) => {
    setStoreNoteId(noteId);
    setStoreAchievementId(null);
    setStoreKbRelationFilter("全部");
    setStoreKbPickId(kbList[0]?.id ?? null);
  };

  const handleConfirmStoreToKb = () => {
    if (!storeKbPickId) return;
    const kb = kbList.find((k) => k.id === storeKbPickId);
    if (!kb) return;
    if (!storeAchievementId && !storeNoteId) return;
    setKbList((prev) => {
      const next = prev.map((k) => (k.id === storeKbPickId ? { ...k, fileCount: k.fileCount + 1 } : k));
      saveKnowledgeBases(next);
      return next;
    });
    const title = storeAchievementId ? storeAchievement?.title : storeNote?.title;
    showToast(`「${title ?? "内容"}」已加入「${kb.name}」`);
    closeStoreModal();
  };

  const handleConfirmDeleteAchievement = () => {
    if (!deleteAchievementTarget) return;
    setAchievements((prev) => prev.filter((a) => a.id !== deleteAchievementTarget.id));
    setDeleteAchievementTarget(null);
    showToast("已删除该成果");
  };

  const handleCreate = (data: { name: string; description: string }) => {
    const newKb: KnowledgeBase = {
      id: `kb${Date.now()}`,
      name: data.name,
      description: data.description || "暂无描述",
      coverImage:
        "https://readdy.ai/api/search-image?query=abstract%20minimalist%20scientific%20research%20knowledge%20clean%20gradient%20calm%20professional%20soft%20background%20neutral&width=600&height=340&seq=kbdefault&orientation=landscape",
      tags: [autoTagFromName(data.name)],
      updatedAt: new Date().toISOString().replace("T", " ").slice(0, 16),
      size: "0KB",
      fileCount: 0,
      memberCount: 1,
      relation: "created",
    };
    setKbList((prev) => {
      const next = [newKb, ...prev];
      saveKnowledgeBases(next);
      return next;
    });
    navigate(`/knowledge/${newKb.id}`);
  };

  const handleDelete = (id: string) => {
    const kb = kbList.find((k) => k.id === id);
    setDeleteKbTarget({ id, name: kb?.name ?? "该知识库" });
  };

  const handleConfirmDeleteKb = () => {
    if (!deleteKbTarget) return;
    setKbList(() => deleteKnowledgeBase(deleteKbTarget.id));
    setDeleteKbTarget(null);
    showToast("已删除该知识库");
  };
  const handleRename = (id: string, name: string) => setKbList(() => renameKnowledgeBase(id, name));

  const handleCreateNote = () => {
    if (!newNoteTitle.trim()) return;
    const id = `n${Date.now()}`;
    // 写入可持久的笔记（编辑页读取），默认正文为空
    upsertNoteDoc(createEmptyNoteDoc(id, newNoteTitle.trim()));
    const note: Note = {
      id,
      title: newNoteTitle.trim(),
      content: "",
      date: new Date().toISOString().slice(0, 10),
      type: "笔记",
      tags: [],
      icon: NOTE_ICON,
      color: NOTE_COLOR,
      bg: NOTE_BG,
      wordCount: 0,
    };
    setNotes((prev) => [note, ...prev]);
    setNewNoteTitle("");
    setShowNewNote(false);
    // 创建后直接进入编辑页（空白编辑）
    navigate(`/knowledge/note/${id}/edit`);
  };

  const handleRenameNote = (id: string) => {
    if (!renameVal.trim()) return;
    setNotes((prev) => prev.map((note) => (note.id === id ? { ...note, title: renameVal.trim() } : note)));
    setRenamingNoteId(null);
    setRenameVal("");
  };

  const handleDeleteNote = (id: string) => {
    setNotes((prev) => prev.filter((note) => note.id !== id));
    setMoreMenuNoteId(null);
  };

  const renderEmpty = (title: string, desc: string, icon: ReactNode) => (
    <div className="flex min-h-[360px] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card text-center shadow-sm">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">{icon}</div>
      <h3 className="text-lg font-semibold tracking-tight text-foreground">{title}</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">{desc}</p>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-white text-foreground">
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className="flex min-w-0 flex-1 flex-col overflow-hidden bg-transparent">
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-[1100px] space-y-5">
            <div className="flex items-center justify-between gap-4 rounded-3xl border border-white/60 bg-white py-3 backdrop-blur">
              <div className="flex items-center gap-8 px-2">
                {mainTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveMain(tab)}
                    className={`text-[18px] transition-colors ${activeMain === tab ? "font-semibold text-foreground" : "font-normal text-muted-foreground hover:text-foreground"}`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <button
                onClick={() => navigate("/cognet")}
                className="inline-flex items-center gap-2 rounded-full bg-[#F7F8FA] px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <Brain className="h-4 w-4" />
                认知网络
              </button>
            </div>

            {activeMain === "知识" && (
              <div className="space-y-6 pb-4">
                <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold tracking-tight text-foreground">我的知识</h2>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">{kbList.length}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">集中管理文献、文档与对话沉淀，支持检索、协作与权限分享。</p>
                  </div>
                  <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center lg:w-auto lg:justify-end">
                    <div className="flex flex-wrap items-center gap-2">
                      {knowledgeTabs.map((tab) => (
                        <button
                          key={tab}
                          type="button"
                          onClick={() => setKnowledgeFilter(tab)}
                          className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                            knowledgeFilter === tab
                              ? "border-primary bg-primary text-primary-foreground shadow-sm"
                              : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          }`}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:flex-initial lg:justify-end">
                      <div className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg border border-input bg-background px-3 shadow-sm sm:min-w-[200px] sm:max-w-[280px]">
                        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <input
                          type="text"
                          value={searchVal}
                          onChange={(e) => setSearchVal(e.target.value)}
                          placeholder="搜索知识库..."
                          className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        />
                      </div>
                      <div className="relative shrink-0">
                        <button
                          type="button"
                          onClick={() => setNewMenuOpen((prev) => !prev)}
                          className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
                        >
                          <Plus className="h-4 w-4" />
                          新建
                          <ArrowDown className="h-4 w-4" />
                        </button>
                        {newMenuOpen && (
                          <div className="absolute right-0 top-full z-20 mt-2 min-w-44 overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-lg">
                            {[
                              { icon: FolderOpen, label: "新建知识库", onClick: () => setShowCreate(true) },
                              { icon: UploadCloud, label: "导入内容", onClick: () => navigate("/knowledge/import") },
                              { icon: Link2, label: "添加链接", onClick: () => navigate("/knowledge/import?method=social") },
                            ].map((item) => {
                              const Icon = item.icon;
                              return (
                                <button
                                  key={item.label}
                                  type="button"
                                  onClick={() => {
                                    item.onClick?.();
                                    setNewMenuOpen(false);
                                  }}
                                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                                >
                                  <Icon className="h-4 w-4 text-muted-foreground" />
                                  {item.label}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {filteredKnowledge.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {filteredKnowledge.map((kb) => (
                      <KnowledgeBaseCard key={kb.id} kb={kb} onDelete={handleDelete} onRename={handleRename} />
                    ))}
                  </div>
                ) : (
                  renderEmpty("暂无知识库", "没有匹配的知识库，尝试清空搜索条件或新建一个知识库。", <FolderOpen className="h-7 w-7" />)
                )}
              </div>
            )}

            {activeMain === "成果" && (
              <div className="space-y-6">
                <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold tracking-tight text-foreground">我的成果</h2>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">{achievements.length}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">由科学对话自动生成，可继续沉淀到知识库或导出分享。</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {achievementTabs.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setAchievementFilter(tab)}
                        className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                          achievementFilter === tab
                            ? "border-primary bg-primary text-primary-foreground shadow-sm"
                            : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>

                {filteredAchievements.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
                    {filteredAchievements.map((item) => {
                      const AcIcon =
                        item.type === "图片" ? Image : item.type === "html" ? FileCode : item.type === "ppt" ? Presentation : FileText;
                      return (
                        <div
                          key={item.id}
                          className="group cursor-pointer rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                          onClick={() => setPreviewAchievementId(item.id)}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${item.badge}`}>
                              <AcIcon className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="truncate text-base font-semibold tracking-tight text-foreground">{item.title}</h3>
                                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                                  {achievementTypeLabel[item.type]}
                                </span>
                              </div>
                              <p className="mt-2 line-clamp-2 text-sm leading-6 text-muted-foreground">{item.desc}</p>
                            </div>
                          </div>
                          <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-sm text-muted-foreground">
                            <span>{item.date}</span>
                            <div className="flex items-center gap-1 opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100">
                              <div className="relative" data-achievement-share>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAchievementShareId((cur) => (cur === item.id ? null : item.id));
                                  }}
                                  className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                                >
                                  <Share2 className="h-3.5 w-3.5" />
                                  分享
                                </button>
                                {achievementShareId === item.id && (
                                  <div
                                    className="absolute bottom-full right-0 z-30 mb-2 min-w-44 overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-lg"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => handleCopyAchievementLink(item)}
                                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                                    >
                                      <Link2 className="h-4 w-4 text-muted-foreground" />
                                      复制分享链接
                                    </button>
                                  </div>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadAchievement(item);
                                }}
                                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                              >
                                <Download className="h-3.5 w-3.5" />
                                下载
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenStoreModal(item.id);
                                }}
                                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                              >
                                <FolderOpen className="h-3.5 w-3.5" />
                                入库
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteAchievementTarget({ id: item.id, title: item.title });
                                }}
                                className="inline-flex items-center gap-1 rounded-lg bg-white px-2.5 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-white hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5 shrink-0 text-destructive" />
                                删除
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  renderEmpty("暂无成果", "当前筛选条件下没有可展示的成果，试试切换分类。", <Image className="h-7 w-7" />)
                )}
              </div>
            )}

            {activeMain === "笔记" && (
              <div className="space-y-6">
                <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold tracking-tight text-foreground">我的笔记</h2>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">{notes.length}</span>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">按类型整理、快速重命名、分享或删除，保持笔记库清晰有序。</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => setShowNewNote(true)}
                      className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
                    >
                      <Plus className="h-4 w-4" />
                      新建笔记
                    </button>
                  </div>
                </div>

                {filteredNotes.length > 0 ? (
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
                    {filteredNotes.map((note) => (
                      <div
                        key={note.id}
                        onClick={() => navigate(`/knowledge/note/${note.id}`)}
                        className="group relative cursor-pointer rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${note.bg}`}>
                            <note.icon className={`text-xl ${note.color}`} />
                          </div>
                          <div className="min-w-0 flex-1">
                            {renamingNoteId === note.id ? (
                              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                <input
                                  autoFocus
                                  value={renameVal}
                                  onChange={(e) => setRenameVal(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleRenameNote(note.id);
                                    if (e.key === "Escape") setRenamingNoteId(null);
                                  }}
                                  className="h-10 flex-1 rounded-lg border border-primary bg-background px-3 text-sm outline-none ring-offset-background focus:ring-2 focus:ring-ring"
                                />
                                <button onClick={() => handleRenameNote(note.id)} className="rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
                                  确定
                                </button>
                                <button onClick={() => setRenamingNoteId(null)} className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                                  取消
                                </button>
                              </div>
                            ) : (
                              <h3 className="truncate text-base font-semibold tracking-tight text-foreground">{note.title}</h3>
                            )}
                            <p className="mt-2 line-clamp-2 text-sm leading-6 text-muted-foreground">{note.content}</p>
                          </div>
                        </div>

                        {note.tags.length > 0 && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {note.tags.map((tag) => (
                              <span key={tag} className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="mt-4 flex items-center justify-between border-t border-border pt-4 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <span>{note.date}</span>
                            {note.wordCount > 0 && <span>{note.wordCount.toLocaleString()} 字</span>}
                          </div>

                          <div className="flex items-center gap-1 opacity-100 transition-opacity lg:opacity-0 lg:group-hover:opacity-100">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenStoreNoteModal(note.id);
                              }}
                              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                            >
                              <FolderOpen className="h-3.5 w-3.5" />
                              入库
                            </button>
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShareNoteId(shareNoteId === note.id ? null : note.id);
                                  setMoreMenuNoteId(null);
                                }}
                                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs transition-colors hover:bg-accent hover:text-accent-foreground"
                              >
                                <Share2 className="h-3.5 w-3.5" />
                                分享
                              </button>
                              {shareNoteId === note.id && (
                                <div
                                  className="absolute bottom-full right-0 z-30 mb-2 min-w-44 overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-lg"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {[
                                    { icon: Link2, label: "复制分享链接" },
                                    { icon: Image, label: "分享为图片" },
                                    { icon: FolderOpen, label: "分享到知识库" },
                                  ].map((s) => {
                                    const SIcon = s.icon;
                                    return (
                                      <button
                                        key={s.label}
                                        onClick={() => setShareNoteId(null)}
                                        className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                                      >
                                        <SIcon className="h-4 w-4 text-muted-foreground" />
                                        {s.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            <div className="relative" ref={moreMenuNoteId === note.id ? moreMenuRef : undefined}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMoreMenuNoteId(moreMenuNoteId === note.id ? null : note.id);
                                  setShareNoteId(null);
                                }}
                                className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs transition-colors hover:bg-accent hover:text-accent-foreground"
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" />
                                更多
                              </button>
                              {moreMenuNoteId === note.id && (
                                <div
                                  className="absolute bottom-full right-0 z-30 mb-2 min-w-36 overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-lg"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() => {
                                      setRenamingNoteId(note.id);
                                      setRenameVal(note.title);
                                      setMoreMenuNoteId(null);
                                    }}
                                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:bg-accent"
                                  >
                                    <Plus className="h-4 w-4 text-muted-foreground" />
                                    重命名
                                  </button>
                                  <button
                                    onClick={() => handleDeleteNote(note.id)}
                                    className="flex w-full items-center gap-2 rounded-lg bg-white px-3 py-2.5 text-left text-sm font-medium text-destructive transition-colors hover:bg-white hover:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 shrink-0 text-destructive" />
                                    删除
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  renderEmpty("暂无笔记", "当前筛选条件下没有笔记，点击右上角新建第一篇。", <Search className="h-7 w-7" />)
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {showCreate && <CreateKBModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />}

      {deleteKbTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={() => setDeleteKbTarget(null)}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">删除知识库</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              确定删除「{deleteKbTarget.name}」吗？此操作不可恢复（演示：会从当前列表与本地存储移除）。
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteKbTarget(null)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteKb}
                className="rounded-lg bg-red-600 px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}

      {(storeAchievementId ? Boolean(storeAchievement) : Boolean(storeNoteId && storeNote)) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={closeStoreModal}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-semibold tracking-tight text-foreground">加入知识库</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  将「{storeAchievementId ? storeAchievement?.title : storeNote?.title}」保存到所选知识库（演示：会增加该库文件计数）。
                </p>
              </div>
              <button
                type="button"
                onClick={closeStoreModal}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                aria-label="关闭"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {kbList.length === 0 ? (
              <p className="mt-6 text-sm text-muted-foreground">暂无知识库，请先在「知识」页新建知识库。</p>
            ) : (
              <>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                  {knowledgeTabs.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setStoreKbRelationFilter(tab)}
                      className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
                        storeKbRelationFilter === tab
                          ? "border-primary bg-primary text-primary-foreground shadow-sm"
                          : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {storeModalKbList.length === 0 ? (
                  <p className="mt-4 text-sm text-muted-foreground">当前筛选下没有知识库，请切换标签或先去「知识」页创建/加入知识库。</p>
                ) : (
                  <ul className="mt-4 max-h-64 space-y-2 overflow-y-auto">
                    {storeModalKbList.map((kb) => (
                      <li key={kb.id}>
                        <button
                          type="button"
                          onClick={() => setStoreKbPickId(kb.id)}
                          className={`flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left text-sm transition-colors ${
                            storeKbPickId === kb.id ? "border-primary bg-primary/5" : "border-border bg-background hover:bg-accent/50"
                          }`}
                        >
                          <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1 truncate font-medium text-foreground">{kb.name}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">{kb.fileCount} 个文件</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button type="button" onClick={closeStoreModal} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent">
                取消
              </button>
              <button
                type="button"
                disabled={!storeKbPickId || kbList.length === 0 || storeModalKbList.length === 0}
                onClick={handleConfirmStoreToKb}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                确定加入
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteAchievementTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm"
          onClick={() => setDeleteAchievementTarget(null)}
        >
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">删除成果</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              确定删除「{deleteAchievementTarget.title}」吗？此操作不可恢复（演示数据将仅从当前列表移除）。
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteAchievementTarget(null)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteAchievement}
                className="rounded-lg bg-red-600 px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-red-700"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}

      {actionToast && (
        <div className="pointer-events-none fixed bottom-8 left-1/2 z-[60] -translate-x-1/2 rounded-full border border-border bg-popover px-4 py-2 text-sm font-medium text-popover-foreground shadow-lg">
          {actionToast}
        </div>
      )}

      {previewAchievement && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={closePreview}>
          <div className="w-full max-w-4xl rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="truncate text-lg font-semibold tracking-tight text-foreground">{previewAchievement.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{previewAchievement.desc}</p>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                aria-label="关闭"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6">
              {renderAchievementPreview(previewAchievement)}
            </div>

            <div className="mt-6 flex items-center justify-end gap-2 border-t border-border pt-4">
              <button
                type="button"
                onClick={() => {
                  closePreview();
                  navigate(`/knowledge/achievement/${previewAchievement.id}`);
                }}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
              >
                打开成果
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewNote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={() => setShowNewNote(false)}>
          <div className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold tracking-tight">新建笔记</h3>
                <p className="mt-1 text-sm text-muted-foreground">填写标题后直接进入编辑页。</p>
              </div>
              <button
                onClick={() => setShowNewNote(false)}
                className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">笔记标题</label>
                <input
                  autoFocus
                  value={newNoteTitle}
                  onChange={(e) => setNewNoteTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateNote();
                  }}
                  placeholder="输入笔记标题..."
                  className="h-11 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none ring-offset-background placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>

            <div className="mt-8 flex items-center justify-end gap-2">
              <button onClick={() => setShowNewNote(false)} className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent">
                取消
              </button>
              <button
                onClick={handleCreateNote}
                disabled={!newNoteTitle.trim()}
                className="rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                创建
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
